/* ============================================================================
   205 Holdings — formatting.

   The rule that governs this whole file: **money is exact**. Every amount
   arrives as a decimal string and is formatted by manipulating those digits.
   `parseFloat` is never used on an amount, and `moneyToFloat` — the one
   escape hatch — is named so it cannot be reached for by accident and is
   only ever used to compute a pixel.

   The second rule: **absent is not zero.** The `*Maybe` variants render an
   em-dash for `null`, and screens should prefer them wherever the backend
   types say `| null`.
   ========================================================================= */

import type { IsoDate, IsoDateTime, MoneyString, Percent, RangePreset, DateRange, WeekdayIndex } from './types';

/** What a field the vendor does not report looks like. Never `0`. */
export const DASH = '—';

/** Every venue and every business day is anchored here. */
export const TZ = 'America/Chicago';

/** The unified business day starts at 05:00 America/Chicago. */
export const BUSINESS_DAY_START_HOUR = 5;

/* ------------------------------------------------------------- money ----- */

interface DecimalParts { neg: boolean; int: string; frac: string }

/** Split a decimal string into sign, integer digits and fraction digits. */
function parts(s: MoneyString): DecimalParts {
  const t = (s ?? '').trim();
  const neg = t.startsWith('-');
  const body = (neg || t.startsWith('+')) ? t.slice(1) : t;
  const [rawInt = '0', rawFrac = ''] = body.split('.');
  const int = rawInt.replace(/\D/g, '') || '0';
  const frac = rawFrac.replace(/\D/g, '');
  return { neg, int, frac };
}

function group(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** Round a fraction string to `places`, carrying into the integer part. */
function roundParts(p: DecimalParts, places: number): DecimalParts {
  if (p.frac.length <= places) {
    return { ...p, frac: p.frac.padEnd(places, '0') };
  }
  const keep = p.frac.slice(0, places);
  const next = p.frac.charCodeAt(places) - 48;
  if (next < 5) return { ...p, frac: keep };

  // Carry, as digit arithmetic — no float anywhere near this.
  const carried = (BigInt(p.int + keep) + 1n).toString().padStart(p.int.length + places, '0');
  const int = places === 0 ? carried : carried.slice(0, carried.length - places);
  const frac = places === 0 ? '' : carried.slice(carried.length - places);
  return { neg: p.neg, int: int || '0', frac };
}

export interface MoneyOptions {
  /** Show cents. Default true. */
  cents?: boolean;
  /** Leading `$`. Default true. */
  symbol?: boolean;
  /** `auto` shows a minus only; `always` shows + or −; `never` prints magnitude. */
  sign?: 'auto' | 'always' | 'never';
  /** Collapse to `$74.9k` / `$1.24M`. Default false. */
  compact?: boolean;
}

/**
 * Format an exact decimal string as money.
 *
 * `formatMoney('74864.74')` → `$74,864.74`
 * `formatMoney('-1200', { sign: 'always' })` → `−$1,200.00`
 * `formatMoney('74864.74', { compact: true })` → `$74.9k`
 *
 * Never parses to a float, so `"0.1"` + `"0.2"` problems cannot occur.
 */
export function formatMoney(s: MoneyString, opts: MoneyOptions = {}): string {
  const { cents = true, symbol = true, sign = 'auto', compact = false } = opts;
  if (compact) return formatMoneyCompact(s, { symbol, sign });

  const p = roundParts(parts(s), cents ? 2 : 0);
  const zero = p.int.replace(/^0+/, '') === '' && p.frac.replace(/0/g, '') === '';
  const body = `${symbol ? '$' : ''}${group(p.int)}${cents ? `.${p.frac}` : ''}`;

  if (sign === 'never' || zero) return body;
  if (p.neg) return `−${body}`;          // U+2212 minus, not a hyphen
  return sign === 'always' ? `+${body}` : body;
}

/**
 * `$74.9k`, `$1.24M`, `$27.8M`. For axis labels and tight tiles only — the
 * full figure must always be reachable (a title attribute, a detail row).
 */
export function formatMoneyCompact(
  s: MoneyString,
  opts: { symbol?: boolean; sign?: 'auto' | 'always' | 'never' } = {},
): string {
  const { symbol = true, sign = 'auto' } = opts;
  const p = parts(s);
  const digits = p.int.replace(/^0+/, '') || '0';
  const units: Array<[number, string]> = [[10, 'B'], [7, 'M'], [4, 'k']];

  let body: string;
  if (digits === '0') {
    body = `${symbol ? '$' : ''}0`;
  } else {
    const unit = units.find(([len]) => digits.length >= len);
    if (!unit) {
      body = `${symbol ? '$' : ''}${group(digits)}`;
    } else {
      const [, suffix] = unit;
      const scale = suffix === 'B' ? 9 : suffix === 'M' ? 6 : 3;
      const head = digits.slice(0, digits.length - scale) || '0';
      const tailDigits = digits.slice(digits.length - scale);
      // One decimal below 100 of the unit, none above — the way a dashboard reads.
      const wantDecimal = head.length < 3;
      const rounded = roundParts({ neg: false, int: head, frac: tailDigits }, wantDecimal ? 1 : 0);
      const shown = wantDecimal && rounded.frac !== '0'
        ? `${group(rounded.int)}.${rounded.frac}`
        : group(rounded.int);
      body = `${symbol ? '$' : ''}${shown}${suffix}`;
    }
  }

  if (sign === 'never' || digits === '0') return body;
  if (p.neg) return `−${body}`;
  return sign === 'always' ? `+${body}` : body;
}

/**
 * The default renderer for any `MoneyString | null` field: an em-dash when the
 * vendor does not report it. Screens should reach for this first.
 */
export function formatMoneyMaybe(s: MoneyString | null | undefined, opts?: MoneyOptions): string {
  return s == null ? DASH : formatMoney(s, opts);
}

/** `+$1,200.00` / `−$450.00`. For deltas and over/short. */
export function formatSignedMoney(s: MoneyString, opts: MoneyOptions = {}): string {
  return formatMoney(s, { ...opts, sign: 'always' });
}

/* --- exact arithmetic on money strings ----------------------------------- */

/** Scale two decimal strings to a common fraction length, as BigInt. */
function align(a: MoneyString, b: MoneyString): { a: bigint; b: bigint; scale: number } {
  const pa = parts(a), pb = parts(b);
  const scale = Math.max(pa.frac.length, pb.frac.length, 2);
  const toInt = (p: DecimalParts) =>
    BigInt((p.neg ? '-' : '') + p.int + p.frac.padEnd(scale, '0'));
  return { a: toInt(pa), b: toInt(pb), scale };
}

function fromBigInt(v: bigint, scale: number): MoneyString {
  const neg = v < 0n;
  const digits = (neg ? -v : v).toString().padStart(scale + 1, '0');
  const int = digits.slice(0, digits.length - scale);
  const frac = scale === 0 ? '' : digits.slice(digits.length - scale);
  return `${neg ? '-' : ''}${int}${frac ? `.${frac}` : ''}`;
}

export function moneyAdd(a: MoneyString, b: MoneyString): MoneyString {
  const { a: x, b: y, scale } = align(a, b);
  return fromBigInt(x + y, scale);
}

export function moneySub(a: MoneyString, b: MoneyString): MoneyString {
  const { a: x, b: y, scale } = align(a, b);
  return fromBigInt(x - y, scale);
}

export function moneySum(list: Array<MoneyString | null | undefined>): MoneyString {
  return list.reduce<MoneyString>((acc, m) => (m == null ? acc : moneyAdd(acc, m)), '0.00');
}

/** −1, 0 or 1. */
export function moneyCompare(a: MoneyString, b: MoneyString): -1 | 0 | 1 {
  const { a: x, b: y } = align(a, b);
  return x < y ? -1 : x > y ? 1 : 0;
}

export function moneyIsZero(s: MoneyString | null | undefined): boolean {
  if (s == null) return false;              // absent is NOT zero
  const p = parts(s);
  return (p.int.replace(/0/g, '') === '') && (p.frac.replace(/0/g, '') === '');
}

export function moneyIsNegative(s: MoneyString | null | undefined): boolean {
  return s != null && parts(s).neg && !moneyIsZero(s);
}

export function moneyAbs(s: MoneyString): MoneyString {
  const p = parts(s);
  return `${p.int}${p.frac ? `.${p.frac}` : ''}`;
}

export function moneyNegate(s: MoneyString): MoneyString {
  return moneyIsNegative(s) ? moneyAbs(s) : `-${moneyAbs(s)}`;
}

export function moneyMax(list: MoneyString[]): MoneyString | null {
  return list.reduce<MoneyString | null>(
    (best, m) => (best === null || moneyCompare(m, best) > 0 ? m : best), null);
}

/**
 * The ONLY float conversion in the app, and it exists for geometry: bar
 * heights, sparkline paths, heatmap intensity. Never render its output.
 */
export function moneyToFloat(s: MoneyString | null | undefined): number {
  if (s == null) return 0;
  const p = parts(s);
  const v = Number(`${p.int}.${p.frac || '0'}`);
  return p.neg ? -v : v;
}

/** Share of `total` that `part` represents, as a percentage. */
export function moneySharePct(part: MoneyString, total: MoneyString): Percent | null {
  if (moneyIsZero(total)) return null;
  const t = moneyToFloat(total);
  if (t === 0) return null;
  return (moneyToFloat(part) / t) * 100;
}

/* ------------------------------------------------------- numbers & pct --- */

export function formatCount(n: number): string {
  return group(Math.round(Math.abs(n)).toString()).replace(/^/, n < 0 ? '−' : '');
}

export function formatCountMaybe(n: number | null | undefined): string {
  return n == null ? DASH : formatCount(n);
}

/** `1.2k`, `18.4k` — for dense tiles. */
export function formatCountCompact(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '−' : '';
  if (abs < 1000) return `${sign}${Math.round(abs)}`;
  if (abs < 1_000_000) return `${sign}${trimZero(abs / 1000)}k`;
  return `${sign}${trimZero(abs / 1_000_000)}M`;
}

function trimZero(v: number): string {
  const s = v < 100 ? v.toFixed(1) : Math.round(v).toString();
  return s.endsWith('.0') ? s.slice(0, -2) : s;
}

/** `12.4%`. Input is already a percentage, not a ratio. */
export function formatPct(p: Percent, digits = 1): string {
  const sign = p < 0 ? '−' : '';
  return `${sign}${Math.abs(p).toFixed(digits)}%`;
}

export function formatPctMaybe(p: Percent | null | undefined, digits = 1): string {
  return p == null ? DASH : formatPct(p, digits);
}

/** `+12.4%` / `−8.0%`. A null baseline has no percentage — returns an em-dash. */
export function formatSignedPct(p: Percent | null | undefined, digits = 1): string {
  if (p == null) return DASH;
  const sign = p > 0 ? '+' : p < 0 ? '−' : '';
  return `${sign}${Math.abs(p).toFixed(digits)}%`;
}

/* -------------------------------------------------------------- dates ---- */

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAYS_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * Parse `YYYY-MM-DD` as a calendar date with no timezone involved. Using
 * `new Date('2026-09-05')` would parse as UTC midnight and print as the 4th
 * for anyone west of Greenwich — the classic off-by-one-night bug.
 */
export function parseIsoDate(d: IsoDate): { y: number; m: number; d: number } {
  const [y, m, day] = d.split('-').map(Number);
  return { y: y || 1970, m: m || 1, d: day || 1 };
}

function utc(d: IsoDate): Date {
  const { y, m, d: day } = parseIsoDate(d);
  return new Date(Date.UTC(y, m - 1, day));
}

/** Monday = 0 … Sunday = 6. */
export function weekdayIndex(d: IsoDate): WeekdayIndex {
  return ((utc(d).getUTCDay() + 6) % 7) as WeekdayIndex;
}

export function weekdayLabel(i: WeekdayIndex, long = false): string {
  return (long ? WEEKDAYS_LONG : WEEKDAYS_SHORT)[i] ?? '';
}

export type DateStyle =
  | 'short'      // 5 Sep
  | 'medium'     // Sat 5 Sep
  | 'long'       // Saturday 5 September 2026
  | 'numeric'    // 09/05/26
  | 'month'      // September 2026
  | 'iso';       // 2026-09-05

export function formatDate(d: IsoDate, style: DateStyle = 'medium'): string {
  const { y, m, d: day } = parseIsoDate(d);
  const wd = weekdayIndex(d);
  switch (style) {
    case 'iso': return d;
    case 'short': return `${day} ${MONTHS_SHORT[m - 1]}`;
    case 'numeric': return `${String(m).padStart(2, '0')}/${String(day).padStart(2, '0')}/${String(y).slice(2)}`;
    case 'month': return `${MONTHS_LONG[m - 1]} ${y}`;
    case 'long': return `${WEEKDAYS_LONG[wd]} ${day} ${MONTHS_LONG[m - 1]} ${y}`;
    case 'medium':
    default: return `${WEEKDAYS_SHORT[wd]} ${day} ${MONTHS_SHORT[m - 1]}`;
  }
}

export function formatDateMaybe(d: IsoDate | null | undefined, style: DateStyle = 'medium'): string {
  return d == null ? DASH : formatDate(d, style);
}

/** `1 – 30 Sep 2026`, collapsing the parts the two ends share. */
export function formatDateRange(from: IsoDate, to: IsoDate): string {
  const a = parseIsoDate(from), b = parseIsoDate(to);
  if (from === to) return `${a.d} ${MONTHS_SHORT[a.m - 1]} ${a.y}`;
  if (a.y === b.y && a.m === b.m) return `${a.d}–${b.d} ${MONTHS_SHORT[a.m - 1]} ${a.y}`;
  if (a.y === b.y) return `${a.d} ${MONTHS_SHORT[a.m - 1]} – ${b.d} ${MONTHS_SHORT[b.m - 1]} ${a.y}`;
  return `${a.d} ${MONTHS_SHORT[a.m - 1]} ${a.y} – ${b.d} ${MONTHS_SHORT[b.m - 1]} ${b.y}`;
}

/* --- instants, always rendered in venue time ----------------------------- */

const timeFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ, hour: 'numeric', minute: '2-digit', hour12: true,
});
const time24Fmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false,
});

/** `11:42 PM` in America/Chicago. */
export function formatTime(ts: IsoDateTime): string {
  return timeFmt.format(new Date(ts)).replace(/ /g, ' ');
}

/** `23:42` — for the "as of HH:MM" stale label, which must be unambiguous. */
export function formatTime24(ts: IsoDateTime): string {
  return time24Fmt.format(new Date(ts));
}

export function formatTimeMaybe(ts: IsoDateTime | null | undefined): string {
  return ts == null ? DASH : formatTime(ts);
}

export function formatDateTime(ts: IsoDateTime): string {
  return `${formatDate(isoDateOf(ts))} · ${formatTime(ts)}`;
}

/* ------------------------------------------------------- the wall clock --- */

/**
 * The chrome clock, always in America/Chicago whatever the device is set to.
 *
 * A screen propped in the back office, a laptop that travelled, a phone still
 * on Eastern after a flight — all of them must read the clock the venues
 * actually run on, because every business date, every 05:00 boundary and every
 * "so far today" on this platform is Central. A clock showing the viewer's own
 * timezone next to figures cut on Chicago's would be worse than no clock.
 *
 * `timeZoneName: 'short'` resolves to CDT or CST on its own, so the label is
 * right on both sides of the daylight-saving change without a rule here.
 */
const CLOCK_DATE = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ, weekday: 'short', day: 'numeric', month: 'short',
});
const CLOCK_TIME = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ, hour: 'numeric', minute: '2-digit', second: '2-digit',
  hour12: true, timeZoneName: 'short',
});
const CLOCK_TIME_SHORT = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ, hour: 'numeric', minute: '2-digit', hour12: true,
});

/** `Fri 18 Sep`, in Central. */
export function clockDate(d: Date = new Date()): string {
  return CLOCK_DATE.format(d);
}

/** `2:47:05 PM CDT` — the full wall clock, seconds and zone included. */
export function clockTime(d: Date = new Date()): string {
  return CLOCK_TIME.format(d);
}

/** `2:47 PM` — for a phone header, where the zone is on the line above. */
export function clockTimeShort(d: Date = new Date()): string {
  return CLOCK_TIME_SHORT.format(d);
}

/** The America/Chicago calendar date an instant falls on. */
export function isoDateOf(ts: IsoDateTime | Date): IsoDate {
  const d = typeof ts === 'string' ? new Date(ts) : ts;
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
  return f.format(d);
}

/**
 * The unified business date for an instant: America/Chicago, minus five
 * hours. 2am Sunday belongs to Saturday night's trade.
 */
export function businessDateOf(ts: IsoDateTime | Date = new Date()): IsoDate {
  const d = typeof ts === 'string' ? new Date(ts) : ts;
  return isoDateOf(new Date(d.getTime() - BUSINESS_DAY_START_HOUR * 3600_000));
}

/** `3m ago`, `2h ago`, `Sat 5 Sep`. */
export function formatRelative(ts: IsoDateTime, now: Date = new Date()): string {
  const diff = now.getTime() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(isoDateOf(ts));
}

/** Age in whole minutes — the number the stale badge is built from. */
/**
 * The time a figure was last true, stamped so it cannot be misread.
 *
 * `formatTime24` alone prints `12:26` for a source that has been dead for a
 * fortnight, and "no data since 12:26" reads as "since this morning" — the
 * opposite of the alarm it is meant to raise. So anything not from today
 * carries its date.
 */
export function formatAsOf(ts: IsoDateTime, now: Date = new Date()): string {
  const day = isoDateOf(ts);
  if (day === isoDateOf(now)) return formatTime24(ts);
  const sameYear = day.slice(0, 4) === isoDateOf(now).slice(0, 4);
  return `${formatDate(day, sameYear ? 'short' : 'medium')} ${formatTime24(ts)}`;
}

export function minutesSince(ts: IsoDateTime, now: Date = new Date()): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(ts).getTime()) / 60_000));
}

/** `9p`, `12a`, `2a` — hour labels for a heatmap axis. */
export function formatHour(h: number): string {
  const hour = ((h % 24) + 24) % 24;
  const suffix = hour < 12 ? 'a' : 'p';
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}${suffix}`;
}

/** `9:00 PM – 10:00 PM`, for a tooltip. */
export function formatHourRange(h: number): string {
  const long = (x: number) => {
    const hour = ((x % 24) + 24) % 24;
    const suffix = hour < 12 ? 'AM' : 'PM';
    const twelve = hour % 12 === 0 ? 12 : hour % 12;
    return `${twelve}:00 ${suffix}`;
  };
  return `${long(h)} – ${long(h + 1)}`;
}

/* --- date arithmetic on IsoDate ------------------------------------------ */

function toIso(d: Date): IsoDate {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

export function addDays(d: IsoDate, n: number): IsoDate {
  const x = utc(d);
  x.setUTCDate(x.getUTCDate() + n);
  return toIso(x);
}

export function addMonths(d: IsoDate, n: number): IsoDate {
  const { y, m, d: day } = parseIsoDate(d);
  const x = new Date(Date.UTC(y, m - 1 + n, 1));
  const lastDay = new Date(Date.UTC(x.getUTCFullYear(), x.getUTCMonth() + 1, 0)).getUTCDate();
  x.setUTCDate(Math.min(day, lastDay));
  return toIso(x);
}

/** Whole days from `a` to `b`; negative when `b` is earlier. */
export function daysBetween(a: IsoDate, b: IsoDate): number {
  return Math.round((utc(b).getTime() - utc(a).getTime()) / 86_400_000);
}

/** Every date in `[from, to]`, inclusive. */
export function eachDay(from: IsoDate, to: IsoDate): IsoDate[] {
  const out: IsoDate[] = [];
  for (let d = from; daysBetween(d, to) >= 0; d = addDays(d, 1)) out.push(d);
  return out;
}

export function startOfMonth(d: IsoDate): IsoDate {
  const { y, m } = parseIsoDate(d);
  return `${y}-${String(m).padStart(2, '0')}-01`;
}

export function startOfQuarter(d: IsoDate): IsoDate {
  const { y, m } = parseIsoDate(d);
  const q = Math.floor((m - 1) / 3) * 3 + 1;
  return `${y}-${String(q).padStart(2, '0')}-01`;
}

export function startOfYear(d: IsoDate): IsoDate {
  return `${parseIsoDate(d).y}-01-01`;
}

/** The earliest date any venue has data for. Rounders opened the books here. */
export const HISTORY_START: IsoDate = '2009-06-05';

/**
 * Resolve a preset against tonight's business date. `custom` returns the
 * fallback range unchanged, so a picker can hold user dates.
 */
export function rangeForPreset(
  preset: RangePreset,
  today: IsoDate = businessDateOf(),
  custom?: { from: IsoDate; to: IsoDate },
): DateRange {
  const mk = (from: IsoDate, to: IsoDate, label: string): DateRange => ({ from, to, preset, label });
  switch (preset) {
    case 'tonight':   return mk(today, today, 'Tonight');
    case 'yesterday': return mk(addDays(today, -1), addDays(today, -1), 'Last night');
    case 'last_7':    return mk(addDays(today, -6), today, 'Last 7 nights');
    case 'last_30':   return mk(addDays(today, -29), today, 'Last 30 nights');
    case 'mtd':       return mk(startOfMonth(today), today, 'Month to date');
    case 'qtd':       return mk(startOfQuarter(today), today, 'Quarter to date');
    case 'ytd':       return mk(startOfYear(today), today, 'Year to date');
    case 'last_year': return mk(startOfYear(addDays(startOfYear(today), -1)), addDays(startOfYear(today), -1), 'Last year');
    case 'all_time':  return mk(HISTORY_START, today, 'All time');
    case 'custom':
    default:
      return {
        from: custom?.from ?? addDays(today, -29),
        to: custom?.to ?? today,
        preset: 'custom',
        label: formatDateRange(custom?.from ?? addDays(today, -29), custom?.to ?? today),
      };
  }
}

/* ------------------------------------------------------------- misc ------ */

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Two-letter initials for an avatar chip. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** `pluralize(1, 'night')` → `1 night`; `pluralize(3, 'night')` → `3 nights`. */
export function pluralize(n: number, word: string, plural?: string): string {
  return `${formatCount(n)} ${n === 1 ? word : plural ?? `${word}s`}`;
}
